import type { AlertItem } from "../../api/zekay/alerts";
import { TrainingStatusCode } from "../../api/training/Training.typed";
import type { TrainingAlertGroup } from "../../api/training/Training.typed";
import { trainingAlertTitle } from "../../components/trainings/common/trainingAlerts";
import type { UserWithRole } from "../../api/auth/Auth.typed";
import type {
  UserDocumentsAlertBatchItem,
  UserDocumentsAlertBatchResponseItem,
} from "../../api/users/User.typed";
import type {
  AlertModelRule,
  EquipmentModel,
  EquipmentSearchRequest,
} from "../../api/equipment/Equipment.typed";
import { LeaveStatus } from "../../api/leave/Leave.typed";
import type { PaginatedResponse } from "../../api/zekay/pagination";
import { extractDataFromResponse, extractPaginationMetadata } from "../../utils/paginationHelpers";

const BATCH_SIZE = 20;

/** Reads rule fields whether the API uses PascalCase or camelCase JSON keys. */
function isEquipmentAlertRuleEnabled(r: AlertModelRule): boolean {
  const o = r as unknown as Record<string, unknown>;
  if (typeof r.Enabled === "boolean") return r.Enabled;
  if (typeof o.enabled === "boolean") return o.enabled;
  return true;
}

function coerceEquipmentRuleOperatorValue(rule: AlertModelRule): {
  operator: unknown;
  value: unknown;
} {
  const o = rule as unknown as Record<string, unknown>;
  return {
    operator: rule.Operator ?? o.operator,
    value: rule.Value ?? o.value,
  };
}

function equipmentAlertOperatorForUi(raw: unknown): string {
  return typeof raw === "string" ? raw : String(raw ?? "");
}

function equipmentAlertThresholdForUi(rule: AlertModelRule, raw: unknown): number {
  const n = Number(raw);
  if (Number.isFinite(n)) return n;
  const o = rule as unknown as Record<string, unknown>;
  const fb = Number(rule.Value ?? o.value);
  return Number.isFinite(fb) ? fb : 0;
}

/** Compares stock count to threshold; guards NaN and normalizes operators (trim, "<>" as "!="). */
function evaluateEquipmentModelAlert(
  totalRaw: unknown,
  operatorRaw: unknown,
  thresholdRaw: unknown,
): boolean {
  const total = Number(totalRaw);
  const threshold = Number(thresholdRaw);
  if (!Number.isFinite(total) || !Number.isFinite(threshold)) return false;
  const op = String(operatorRaw ?? "").trim();
  switch (op) {
    case "=":
      return total === threshold;
    case "!=":
    case "<>":
      return total !== threshold;
    case ">":
      return total > threshold;
    case ">=":
      return total >= threshold;
    case "<":
      return total < threshold;
    case "<=":
      return total <= threshold;
    default:
      return false;
  }
}

type InventoryDeps = {
  getEquipmentModels: () => Promise<unknown>;
  searchEquipmentsBatch: (
    reqs: EquipmentSearchRequest[],
  ) => Promise<Array<{ total: number }> | undefined>;
};

type HRDeps = {
  getLeaveRequests: (q: { Status: number; Page: number; Limit: number }) => Promise<unknown>;
};

type TrainingAlertDeps = {
  getTrainingAlerts: () => Promise<TrainingAlertGroup[] | undefined>;
};

const TRAINING_ALERT_CATEGORY = "Formations";

type UserDocumentsAlertDeps = {
  getUsers: () => Promise<UserWithRole[] | undefined>;
  getUserDocumentsAlertBatch: (
    items: UserDocumentsAlertBatchItem[],
  ) => Promise<UserDocumentsAlertBatchResponseItem[] | undefined>;
};

const USER_DOCS_BATCH_API = 20;

type GenerateAlertsOptions = {
  ShouldContinue?: () => boolean;
};

export async function generateInventoryAlerts(
  { getEquipmentModels, searchEquipmentsBatch }: InventoryDeps,
  Options?: GenerateAlertsOptions,
): Promise<AlertItem[]> {
  const models = await getEquipmentModels();
  const modelsList = extractDataFromResponse(
    models as PaginatedResponse<EquipmentModel> | EquipmentModel[] | undefined,
  ) as EquipmentModel[];
  const requests: EquipmentSearchRequest[] = [];
  const items: { model: EquipmentModel; rule: AlertModelRule }[] = [];

  for (const model of modelsList) {
    if (Options?.ShouldContinue && !Options.ShouldContinue()) return [];
    const modelAlerts: AlertModelRule[] = (model.Alerts || []).filter((r: AlertModelRule) =>
      isEquipmentAlertRuleEnabled(r),
    );
    if (!modelAlerts.length) continue;

    for (const rule of modelAlerts) {
      if (Options?.ShouldContinue && !Options.ShouldContinue()) return [];
      requests.push({
        ModelID: model.ID!,
        Page: 1,
        Limit: 1,
        Filters: rule.Filters || undefined,
      });
      items.push({ model, rule });
    }
  }

  if (requests.length === 0) return [];

  const createDefaultAlert = (
    items: { model: EquipmentModel; rule: AlertModelRule }[],
    index: number,
  ): AlertItem => {
    const { model, rule } = items[index];
    const { operator: opRaw, value: thrRaw } = coerceEquipmentRuleOperatorValue(rule);
    return {
      id: rule.ID,
      name: rule.Name,
      count: 0,
      severity: rule.Severity,
      triggered: false,
      operator: equipmentAlertOperatorForUi(opRaw),
      value: equipmentAlertThresholdForUi(rule, thrRaw),
      category: "Équipement",
      target: {
        kind: "equipment",
        modelId: model.ID!,
        modelName: model.Name,
        filters: rule.Filters || undefined,
      },
    };
  };

  const combined: AlertItem[] = [];

  for (let i = 0; i < requests.length; i += BATCH_SIZE) {
    if (Options?.ShouldContinue && !Options.ShouldContinue()) return combined;
    const batch = requests.slice(i, i + BATCH_SIZE);
    const batchItems = items.slice(i, i + BATCH_SIZE);
    try {
      const results = await searchEquipmentsBatch(batch);
      if (!results || results.length !== batchItems.length) {
        for (let j = 0; j < batchItems.length; j++)
          combined.push(createDefaultAlert(batchItems, j));
        continue;
      }
      for (let j = 0; j < batchItems.length; j++) {
        const { model, rule } = batchItems[j];
        const row = results[j];
        const n = Number(row?.total);
        const countForUi = Number.isFinite(n) ? n : 0;
        const { operator: opRaw, value: thresholdRaw } = coerceEquipmentRuleOperatorValue(rule);
        const triggered = evaluateEquipmentModelAlert(
          Number.isFinite(n) ? n : NaN,
          opRaw,
          thresholdRaw,
        );
        combined.push({
          id: rule.ID,
          name: rule.Name,
          count: countForUi,
          severity: rule.Severity,
          triggered,
          operator: equipmentAlertOperatorForUi(opRaw),
          value: equipmentAlertThresholdForUi(rule, thresholdRaw),
          category: "Équipement",
          target: {
            kind: "equipment",
            modelId: model.ID!,
            modelName: model.Name,
            filters: rule.Filters || undefined,
          },
        });
      }
    } catch {
      for (let j = 0; j < batchItems.length; j++) combined.push(createDefaultAlert(batchItems, j));
    }
  }
  return combined;
}

export async function generateTrainingAlerts({
  getTrainingAlerts,
}: TrainingAlertDeps): Promise<AlertItem[]> {
  const rows = await getTrainingAlerts();
  if (!rows?.length) return [];

  return rows
    .filter((row) => row.count > 0)
    .map((row) => {
      const target = row.target;

      return {
        id: `trainings_${row.key}_${row.training_template_id}_${row.status_code}`,
        name: trainingAlertTitle(row.agency, row.qualification),
        detail: `${row.status} · ${row.short_label || row.label}`,
        count: row.count,
        triggered: true,
        severity: "warning" as const,
        operator: ">",
        value: 0,
        category: TRAINING_ALERT_CATEGORY,
        target: {
          kind: "trainings",
          view: target?.view || (row.status_code === TrainingStatusCode.Done ? "cards" : "list"),
          trainingTemplateId: target?.training_template_id ?? row.training_template_id,
          statusCode: target?.status_code ?? row.status_code,
          alertKey: target?.alert ?? row.key,
          label: row.short_label || row.label,
        },
      };
    });
}

export async function generateHRAlerts({ getLeaveRequests }: HRDeps): Promise<AlertItem[]> {
  const combined: AlertItem[] = [];
  const pendingResp = await getLeaveRequests({ Status: LeaveStatus.PENDING, Page: 1, Limit: 1 });
  const pendingMeta = extractPaginationMetadata(
    pendingResp as PaginatedResponse<unknown> | unknown[] | undefined,
  );
  const pendingTotal = pendingMeta ? pendingMeta.total : 0;
  combined.push({
    id: "hr_pending",
    name: "Demandes à valider",
    count: pendingTotal,
    triggered: pendingTotal > 0,
    severity: pendingTotal > 0 ? "warning" : "info",
    operator: ">",
    value: 0,
    category: "RH",
    target: { kind: "hr" },
  });

  const cancelReqResp = await getLeaveRequests({
    Status: LeaveStatus.CANCELLATION_REQUESTED,
    Page: 1,
    Limit: 1,
  });
  const cancelReqMeta = extractPaginationMetadata(
    cancelReqResp as PaginatedResponse<unknown> | unknown[] | undefined,
  );
  const cancelReqTotal = cancelReqMeta ? cancelReqMeta.total : 0;
  combined.push({
    id: "hr_cancel_requests",
    name: "Annulations à traiter",
    count: cancelReqTotal,
    triggered: cancelReqTotal > 0,
    severity: cancelReqTotal > 0 ? "warning" : "info",
    operator: ">",
    value: 0,
    category: "RH",
    target: { kind: "hr" },
  });
  return combined;
}

export async function generateUserDocumentAlerts(
  { getUsers, getUserDocumentsAlertBatch }: UserDocumentsAlertDeps,
  Options?: GenerateAlertsOptions,
): Promise<AlertItem[]> {
  const users = await getUsers();
  if (!users?.length) return [];

  const perUserAlerts: AlertItem[] = [];
  for (let i = 0; i < users.length; i += USER_DOCS_BATCH_API) {
    if (Options?.ShouldContinue && !Options.ShouldContinue()) return [];
    const slice = users.slice(i, i + USER_DOCS_BATCH_API);
    const items: UserDocumentsAlertBatchItem[] = slice.map(({ user }) => ({
      user_id: user.id,
    }));
    const rows = await getUserDocumentsAlertBatch(items);
    if (!rows || rows.length !== slice.length) continue;
    for (let j = 0; j < slice.length; j++) {
      const wr = slice[j];
      const row = rows[j];
      const docs = Array.isArray(row?.documents) ? row.documents : [];
      const countFromApi = row?.alerts_count;
      const count = typeof countFromApi === "number" ? countFromApi : docs.length;
      if (count <= 0) continue;
      const fullName =
        `${wr.user.first_name ?? ""} ${wr.user.last_name ?? ""}`.trim() ||
        wr.user.email ||
        "Utilisateur";
      perUserAlerts.push({
        id: `settings_user_docs_alert_${wr.user.id}`,
        name: `${fullName} - Documents`,
        count,
        triggered: true,
        severity: "warning",
        operator: ">",
        value: 0,
        category: "Utilisateurs",
        target: { kind: "users", userId: wr.user.id },
      });
    }
  }

  return perUserAlerts.sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
}
